import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent, CalendarEventStatus, CalendarEventStatusDefinition, Employee } from '../types/Calendar';
import type { Contact } from '../../contacts/types/Contact';
import {
  resolveEventStaffIds,
} from '../calendarUtils';
import { LocalizedDateInput, Spinner } from '../../../shared/components';
import { formatLocalizedDateTimeLabel } from '../../../shared/utils/localization';
import {
  applyCalendarEventListPanelFilters,
  buildCalendarEventListRange,
  DEFAULT_CALENDAR_EVENT_LIST_PANEL_FILTERS,
  DEFAULT_CALENDAR_EVENT_LIST_SORT,
  sortCalendarEventList,
  type CalendarEventListPanelFilters,
  type CalendarEventListSort,
  type CalendarEventListSortField,
} from '../utils/calendarEventListFilters';
import { exportCalendarEventsToPdf } from '../utils/calendarEventsExport';
import {
  CALENDAR_EVENTS_PDF_FIELD_KEYS,
  DEFAULT_CALENDAR_EVENTS_PDF_THEME,
  loadCalendarEventsPdfTheme,
  saveCalendarEventsPdfTheme,
  type CalendarEventsPdfFieldKey,
  type CalendarEventsPdfTheme,
} from '../utils/calendarEventsPdfTheme';
import {
  FALLBACK_CALENDAR_EVENT_STATUSES,
  getCalendarEventStatusLabel,
} from '../utils/calendarEventStatuses';
import { listEventStatuses } from '../services/calendarService';
import styles from './CalendarEventsListModal.module.css';

type CalendarEventsListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialFromLocal: string;
  initialToLocal: string;
  roomGroupLabel: string;
  loadEventsForRange: (fromLocal: string, toLocal: string) => Promise<CalendarEvent[]>;
  employees: Employee[];
  contacts: Contact[];
  getRoomDisplayName: (roomId?: string) => string;
  getStaffNames: (staffIds?: string[]) => string;
  getEventDisplayTitle: (event: Pick<CalendarEvent, 'title' | 'appointmentTypeId'>) => string;
  onViewEvent: (event: CalendarEvent) => void;
};

const toDateInputValue = (localDateTime: string) => localDateTime.slice(0, 10);

export const CalendarEventsListModal: React.FC<CalendarEventsListModalProps> = ({
  isOpen,
  onClose,
  initialFromLocal,
  initialToLocal,
  roomGroupLabel,
  loadEventsForRange,
  employees,
  contacts,
  getRoomDisplayName,
  getStaffNames,
  getEventDisplayTitle,
  onViewEvent,
}) => {
  const { t } = useTranslation('calendar');
  const [eventStatuses, setEventStatuses] = React.useState<CalendarEventStatusDefinition[]>(FALLBACK_CALENDAR_EVENT_STATUSES);
  const statusOptions = React.useMemo(
    () => (eventStatuses.length > 0 ? eventStatuses : FALLBACK_CALENDAR_EVENT_STATUSES).map((item) => item.code),
    [eventStatuses],
  );

  React.useEffect(() => {
    let cancelled = false;
    void listEventStatuses()
      .then((items) => {
        if (!cancelled && items.length > 0) {
          setEventStatuses(items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEventStatuses(FALLBACK_CALENDAR_EVENT_STATUSES);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [filters, setFilters] = React.useState<CalendarEventListPanelFilters>(
    DEFAULT_CALENDAR_EVENT_LIST_PANEL_FILTERS,
  );
  const [sort, setSort] = React.useState<CalendarEventListSort>(DEFAULT_CALENDAR_EVENT_LIST_SORT);
  const [rangeStartDate, setRangeStartDate] = React.useState('');
  const [rangeEndDate, setRangeEndDate] = React.useState('');
  const [appliedRange, setAppliedRange] = React.useState<{ fromLocal: string; toLocal: string } | null>(null);
  const [rangeEvents, setRangeEvents] = React.useState<CalendarEvent[]>([]);
  const [rangeError, setRangeError] = React.useState('');
  const [eventsLoading, setEventsLoading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [pdfTheme, setPdfTheme] = React.useState<CalendarEventsPdfTheme>(DEFAULT_CALENDAR_EVENTS_PDF_THEME);
  const [isPdfStyleOpen, setIsPdfStyleOpen] = React.useState(false);

  const loadRangeEvents = React.useCallback(async (startDate: string, endDate: string) => {
    const range = buildCalendarEventListRange(startDate, endDate);
    if (!range) {
      setRangeError(t('eventRangeInvalid'));
      return;
    }

    setRangeError('');
    setEventsLoading(true);
    try {
      const data = await loadEventsForRange(range.fromLocal, range.toLocal);
      setRangeEvents(data);
      setAppliedRange(range);
    } catch (error) {
      setRangeEvents([]);
      setAppliedRange(null);
      setRangeError(error instanceof Error ? error.message : t('failedToLoadEvents'));
    } finally {
      setEventsLoading(false);
    }
  }, [loadEventsForRange, t]);

  React.useEffect(() => {
    if (!isOpen) {
      setFilters(DEFAULT_CALENDAR_EVENT_LIST_PANEL_FILTERS);
      setSort(DEFAULT_CALENDAR_EVENT_LIST_SORT);
      setRangeStartDate('');
      setRangeEndDate('');
      setAppliedRange(null);
      setRangeEvents([]);
      setRangeError('');
      setIsPdfStyleOpen(false);
      return;
    }

    setPdfTheme(loadCalendarEventsPdfTheme());

    const startDate = toDateInputValue(initialFromLocal);
    const endDate = toDateInputValue(initialToLocal);
    setRangeStartDate(startDate);
    setRangeEndDate(endDate);
    void loadRangeEvents(startDate, endDate);
  }, [initialFromLocal, initialToLocal, isOpen, loadRangeEvents]);

  const getStatusLabel = React.useCallback((status: CalendarEventStatus) => {
    return getCalendarEventStatusLabel(status, (key) => t(key), eventStatuses);
  }, [eventStatuses, t]);

  const getContactLabel = React.useCallback((contactId?: string) => {
    if (!contactId) {
      return '-';
    }

    return contacts.find((contact) => contact.id === contactId)?.name || contactId;
  }, [contacts]);

  const dateRangeLabel = React.useMemo(() => {
    if (!appliedRange) {
      return '-';
    }

    return `${formatLocalizedDateTimeLabel(appliedRange.fromLocal)} – ${formatLocalizedDateTimeLabel(appliedRange.toLocal)}`;
  }, [appliedRange]);

  const listEvents = React.useMemo(() => {
    const filtered = applyCalendarEventListPanelFilters(rangeEvents, filters);
    return sortCalendarEventList(filtered, sort, {
      getRoomDisplayName,
      getStaffNames,
      getContactLabel,
      getEventDisplayTitle,
      getStatusLabel,
    });
  }, [filters, getContactLabel, getEventDisplayTitle, getRoomDisplayName, getStaffNames, getStatusLabel, rangeEvents, sort]);

  const handleSort = React.useCallback((field: CalendarEventListSortField) => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const renderSortableHeader = (field: CalendarEventListSortField, label: string) => (
    <th
      key={field}
      aria-sort={sort.field === field ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={styles.sortableHeader}
        onClick={() => handleSort(field)}
      >
        <span>{label}</span>
        {sort.field === field ? (
          <span className={styles.sortIndicator} aria-hidden>{sort.direction === 'asc' ? '▲' : '▼'}</span>
        ) : null}
      </button>
    </th>
  );

  const filterSummary = React.useMemo(() => {
    const parts: string[] = [];

    if (filters.status) {
      parts.push(`${t('status')}: ${getStatusLabel(filters.status)}`);
    }

    if (filters.staffId) {
      const employee = employees.find((item) => item.id === filters.staffId);
      parts.push(`${t('staff')}: ${employee?.name || employee?.email || filters.staffId}`);
    }

    if (filters.contactId) {
      parts.push(`${t('contact')}: ${getContactLabel(filters.contactId)}`);
    }

    if (filters.search.trim()) {
      parts.push(`${t('searchEvents')}: ${filters.search.trim()}`);
    }

    return parts.join(' • ');
  }, [employees, filters, getContactLabel, getStatusLabel, t]);

  const buildExportRows = React.useCallback(() => (
    listEvents.map((event) => ({
      start: formatLocalizedDateTimeLabel(event.startAtUtc),
      end: formatLocalizedDateTimeLabel(event.endAtUtc),
      title: getEventDisplayTitle(event),
      status: getStatusLabel(event.status),
      room: getRoomDisplayName(event.roomId),
      staff: getStaffNames(resolveEventStaffIds(event)),
      contact: getContactLabel(event.contactId),
      notes: event.notes || '',
    }))
  ), [getContactLabel, getEventDisplayTitle, getRoomDisplayName, getStaffNames, getStatusLabel, listEvents]);

  const onApplyDateRange = React.useCallback(() => {
    void loadRangeEvents(rangeStartDate, rangeEndDate);
  }, [loadRangeEvents, rangeEndDate, rangeStartDate]);

  const updatePdfTheme = React.useCallback((updater: (current: CalendarEventsPdfTheme) => CalendarEventsPdfTheme) => {
    setPdfTheme((current) => {
      const next = updater(current);
      saveCalendarEventsPdfTheme(next);
      return next;
    });
  }, []);

  const getPdfFieldLabel = React.useCallback((fieldKey: CalendarEventsPdfFieldKey) => {
    if (fieldKey === 'time') return t('pdfWhenColumn');
    if (fieldKey === 'title') return t('title');
    if (fieldKey === 'status') return t('status');
    if (fieldKey === 'room') return t('room');
    if (fieldKey === 'staff') return t('staff');
    if (fieldKey === 'contact') return t('contact');
    return t('notes');
  }, [t]);

  const onResetPdfTheme = React.useCallback(() => {
    setPdfTheme(DEFAULT_CALENDAR_EVENTS_PDF_THEME);
    saveCalendarEventsPdfTheme(DEFAULT_CALENDAR_EVENTS_PDF_THEME);
  }, []);

  const togglePdfColumnVisibility = React.useCallback((fieldKey: CalendarEventsPdfFieldKey, checked: boolean) => {
    updatePdfTheme((current) => {
      const nextVisibility = {
        ...current.columnVisibility,
        [fieldKey]: checked,
      };
      const enabledCount = CALENDAR_EVENTS_PDF_FIELD_KEYS.filter((key) => nextVisibility[key]).length;
      if (enabledCount === 0) {
        return current;
      }

      return {
        ...current,
        columnVisibility: nextVisibility,
      };
    });
  }, [updatePdfTheme]);

  const onExportPdf = React.useCallback(async () => {
    setIsExporting(true);
    try {
      await exportCalendarEventsToPdf({
        rows: buildExportRows(),
        dateRangeLabel,
        roomGroupLabel,
        filterSummary,
        theme: pdfTheme,
        labels: {
          reportTitle: t('eventListExportTitle'),
          exportedAt: t('exportedAt'),
          dateRange: t('dateRange'),
          roomGroup: t('filterByRoomGroup'),
          filters: t('activeFilters'),
          eventCount: t('eventCount'),
          title: t('title'),
          when: t('pdfWhenColumn'),
          start: t('start'),
          end: t('end'),
          status: t('status'),
          room: t('room'),
          staff: t('staff'),
          contact: t('contact'),
          notes: t('notes'),
          noEvents: t('noEventsInList'),
          page: t('page'),
          of: t('of'),
        },
      });
    } finally {
      setIsExporting(false);
    }
  }, [buildExportRows, dateRangeLabel, filterSummary, pdfTheme, roomGroupLabel, t]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-events-list-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 id="calendar-events-list-title" className={styles.title}>{t('eventList')}</h3>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('close', { ns: 'common' })}
          </button>
        </div>

        <div className={styles.summary}>
          <span><strong>{t('filterByRoomGroup')}:</strong> {roomGroupLabel}</span>
        </div>

        <div className={styles.dateRangeRow}>
          <div className={styles.filterField}>
            <label htmlFor="calendar-list-start-date">{t('from')}</label>
            <LocalizedDateInput
              id="calendar-list-start-date"
              value={rangeStartDate}
              onValueChange={setRangeStartDate}
              aria-label={t('from')}
              fillWidth
            />
          </div>
          <div className={styles.filterField}>
            <label htmlFor="calendar-list-end-date">{t('to')}</label>
            <LocalizedDateInput
              id="calendar-list-end-date"
              value={rangeEndDate}
              onValueChange={setRangeEndDate}
              aria-label={t('to')}
              fillWidth
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onApplyDateRange}
            disabled={eventsLoading}
          >
            {t('apply')}
          </button>
          {rangeError ? <p className={styles.rangeError}>{rangeError}</p> : null}
        </div>

        <div className={styles.filters}>
          <div className={styles.filterField}>
            <label htmlFor="calendar-list-status">{t('status')}</label>
            <select
              id="calendar-list-status"
              value={filters.status}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as CalendarEventStatus | '' }))}
            >
              <option value="">{t('allStatuses')}</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{getStatusLabel(status)}</option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label htmlFor="calendar-list-staff">{t('staff')}</label>
            <select
              id="calendar-list-staff"
              value={filters.staffId}
              onChange={(event) => setFilters((current) => ({ ...current, staffId: event.target.value }))}
            >
              <option value="">{t('allStaff')}</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name || employee.email || employee.id}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label htmlFor="calendar-list-contact">{t('contact')}</label>
            <select
              id="calendar-list-contact"
              value={filters.contactId}
              onChange={(event) => setFilters((current) => ({ ...current, contactId: event.target.value }))}
            >
              <option value="">{t('allContacts')}</option>
              {contacts.map((contact) => (
                <option key={contact.id} value={contact.id}>{contact.name || contact.id}</option>
              ))}
            </select>
          </div>

          <div className={`${styles.filterField} ${styles.searchField}`}>
            <label htmlFor="calendar-list-search">{t('searchEvents')}</label>
            <input
              id="calendar-list-search"
              className="text-input"
              type="search"
              value={filters.search}
              placeholder={t('searchEventsPlaceholder')}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            />
          </div>
        </div>

        <section className={styles.pdfStyleSection}>
          <button
            type="button"
            className={styles.pdfStyleToggle}
            onClick={() => setIsPdfStyleOpen((current) => !current)}
            aria-expanded={isPdfStyleOpen}
          >
            <span>{t('pdfAppearance')}</span>
            <span aria-hidden>{isPdfStyleOpen ? '▾' : '▸'}</span>
          </button>
          {isPdfStyleOpen ? (
            <div className={styles.pdfStylePanel}>
              <p className={styles.pdfStyleHint}>{t('pdfAppearanceHint')}</p>
              <div className={styles.pdfColorGrid}>
                <label className={styles.colorField}>
                  <span>{t('pdfColorHeader')}</span>
                  <input
                    type="color"
                    value={pdfTheme.headerBackground}
                    onChange={(event) => updatePdfTheme((current) => ({
                      ...current,
                      headerBackground: event.target.value,
                    }))}
                  />
                </label>
                <label className={styles.colorField}>
                  <span>{t('pdfColorHeaderText')}</span>
                  <input
                    type="color"
                    value={pdfTheme.headerText}
                    onChange={(event) => updatePdfTheme((current) => ({
                      ...current,
                      headerText: event.target.value,
                    }))}
                  />
                </label>
                <label className={styles.colorField}>
                  <span>{t('pdfColorTableHeader')}</span>
                  <input
                    type="color"
                    value={pdfTheme.tableHeaderBackground}
                    onChange={(event) => updatePdfTheme((current) => ({
                      ...current,
                      tableHeaderBackground: event.target.value,
                    }))}
                  />
                </label>
                <label className={styles.colorField}>
                  <span>{t('pdfColorTableHeaderText')}</span>
                  <input
                    type="color"
                    value={pdfTheme.tableHeaderText}
                    onChange={(event) => updatePdfTheme((current) => ({
                      ...current,
                      tableHeaderText: event.target.value,
                    }))}
                  />
                </label>
                <label className={styles.colorField}>
                  <span>{t('pdfColorRowAlternate')}</span>
                  <input
                    type="color"
                    value={pdfTheme.rowAlternateBackground}
                    onChange={(event) => updatePdfTheme((current) => ({
                      ...current,
                      rowAlternateBackground: event.target.value,
                    }))}
                  />
                </label>
                <label className={styles.colorField}>
                  <span>{t('pdfColorBorder')}</span>
                  <input
                    type="color"
                    value={pdfTheme.borderColor}
                    onChange={(event) => updatePdfTheme((current) => ({
                      ...current,
                      borderColor: event.target.value,
                    }))}
                  />
                </label>
              </div>
              <div className={styles.pdfFieldColorsHeading}>{t('pdfColumns')}</div>
              <div className={styles.pdfColumnGrid}>
                {CALENDAR_EVENTS_PDF_FIELD_KEYS.map((fieldKey) => (
                  <label key={`column-${fieldKey}`} className={styles.columnCheckboxRow}>
                    <input
                      className={styles.columnCheckboxInput}
                      type="checkbox"
                      checked={pdfTheme.columnVisibility[fieldKey]}
                      onChange={(event) => togglePdfColumnVisibility(fieldKey, event.target.checked)}
                    />
                    <span>{getPdfFieldLabel(fieldKey)}</span>
                  </label>
                ))}
              </div>
              <div className={styles.pdfFieldColorsHeading}>{t('pdfFieldColors')}</div>
              <div className={styles.pdfColorGrid}>
                {CALENDAR_EVENTS_PDF_FIELD_KEYS.map((fieldKey) => (
                  <label key={fieldKey} className={styles.colorField}>
                    <span>{getPdfFieldLabel(fieldKey)}</span>
                    <input
                      type="color"
                      value={pdfTheme.fields[fieldKey]}
                      onChange={(event) => updatePdfTheme((current) => ({
                        ...current,
                        fields: {
                          ...current.fields,
                          [fieldKey]: event.target.value,
                        },
                      }))}
                    />
                  </label>
                ))}
              </div>
              <div className={styles.pdfStyleActions}>
                <button type="button" className="btn btn-secondary" onClick={onResetPdfTheme}>
                  {t('resetPdfColors')}
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <div className={styles.tableWrap}>
          {eventsLoading ? (
            <div className={styles.loadingState}>
              <Spinner text={t('refreshingEventData')} />
            </div>
          ) : listEvents.length === 0 ? (
            <div className={styles.emptyState}>{t('noEventsInList')}</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  {renderSortableHeader('start', t('start'))}
                  {renderSortableHeader('end', t('end'))}
                  {renderSortableHeader('title', t('title'))}
                  {renderSortableHeader('status', t('status'))}
                  {renderSortableHeader('room', t('room'))}
                  {renderSortableHeader('staff', t('staff'))}
                  {renderSortableHeader('contact', t('contact'))}
                  {renderSortableHeader('notes', t('notes'))}
                </tr>
              </thead>
              <tbody>
                {listEvents.map((event) => (
                  <tr key={event.id}>
                    <td>{formatLocalizedDateTimeLabel(event.startAtUtc)}</td>
                    <td>{formatLocalizedDateTimeLabel(event.endAtUtc)}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.eventTitleButton}
                        onClick={() => {
                          onViewEvent(event);
                          onClose();
                        }}
                      >
                        {getEventDisplayTitle(event)}
                      </button>
                    </td>
                    <td>{getStatusLabel(event.status)}</td>
                    <td>{getRoomDisplayName(event.roomId)}</td>
                    <td>{getStaffNames(resolveEventStaffIds(event))}</td>
                    <td>{getContactLabel(event.contactId)}</td>
                    <td className={styles.notesCell}>{event.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.countLabel}>
            {appliedRange ? (
              <>
                {t('dateRange')}: {dateRangeLabel}
                {' • '}
              </>
            ) : null}
            {t('eventsShownCount', { count: listEvents.length })}
          </span>
          <div className={styles.footerActions}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('close', { ns: 'common' })}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void onExportPdf()}
              disabled={isExporting || eventsLoading || !appliedRange}
            >
              {isExporting ? t('exportingPdf') : t('exportToPdf')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CalendarEventsListModal;
